import React from 'react';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img
                src="/branding/hole-in-one-logo-3d.png"
                alt="Hole In 1 Logo"
                className="w-12 h-12 object-contain"
              />
              <span className="ml-2 text-xl font-bold text-gray-900">Hole In 1</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <a
            href="/"
            className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors mb-6"
          >
            ← Back to Home
          </a>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-500 mb-2">Effective Date: February 28, 2026</p>
          <p className="text-gray-500 mb-8">Last Updated: February 28, 2026</p>

          <div className="prose prose-lg max-w-none text-gray-700">

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="mb-4">
                Welcome to Hole In 1 ("App," "Services," "we," "us," or "our"), a mobile application operated by Kia Kaha Labs LLC ("Company"). These Terms of Service ("Terms," "Agreement") constitute a legally binding agreement between you ("User," "you," or "your") and Kia Kaha Labs LLC governing your access to and use of the Hole In 1 mobile application and all related services, features, content, and functionality.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">1.1 Agreement to Terms</h3>
              <p className="mb-4">
                By creating an account, accessing, downloading, installing, or using the Hole In 1 app, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy, which is incorporated herein by reference. If you do not agree to these Terms, you must not access or use the Services.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">1.2 Age Requirement</h3>
              <p className="mb-4">
                The Services are intended solely for users who are at least 18 years of age. By using the Services, you represent that you are at least 18 years old and have the legal capacity to enter into this Agreement. We do not knowingly collect information from or direct any of our Services to anyone under the age of 18.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">1.3 Modifications to Terms</h3>
              <p className="mb-4">
                We reserve the right to modify, amend, or update these Terms at any time at our sole discretion. When we make changes, we will update the "Last Updated" date at the top of these Terms and notify you through the App, via email, or by other reasonable means. Material changes will be effective upon notice, and non-material changes will be effective upon posting. Your continued use of the Services after any changes constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">1.4 Additional Terms</h3>
              <p className="mb-4">
                Certain features or services may be subject to additional terms and conditions, which will be presented to you at the time you access such features or services. Those additional terms are incorporated into these Terms by reference.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.1 Overview</h3>
              <p className="mb-4">
                Hole In 1 is a mobile application designed for golf enthusiasts to record, track, and share their golf experiences. The Services include, but are not limited to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>User account creation and profile management</li>
                <li>Recording and uploading golf videos (audio and video content)</li>
                <li>Tracking shot results, hole information, and performance data</li>
                <li>Location-based features including golf course discovery and check-ins</li>
                <li>Social features allowing users to connect, share, and interact</li>
                <li>Subscription-based premium features and content</li>
                <li>Push notifications and communications</li>
                <li>Integration with third-party services and platforms</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.2 Service Availability</h3>
              <p className="mb-4">
                We strive to provide reliable and uninterrupted access to the Services. However, we do not guarantee that the Services will be available at all times or that they will be error-free. The Services may be subject to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Scheduled or unscheduled maintenance and updates</li>
                <li>Technical difficulties, server outages, or connectivity issues</li>
                <li>Modifications, suspension, or discontinuation of features</li>
                <li>Geographic restrictions or limitations</li>
              </ul>
              <p className="mb-4">
                We reserve the right to modify, suspend, or discontinue any aspect of the Services at any time, with or without notice, and without liability to you.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.3 Updates and Changes</h3>
              <p className="mb-4">
                We may release updates, patches, or new versions of the App from time to time. These updates may add, modify, or remove features and functionality. You may be required to install updates to continue using the Services. Your continued use of the Services after any updates constitutes acceptance of the updated Services and any changes to these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts and Registration</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 Account Creation</h3>
              <p className="mb-4">
                To access certain features of the Services, you must create an account. You may register using:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Email address and password</li>
                <li>Apple Sign-In</li>
                <li>Google Sign-In</li>
              </ul>
              <p className="mb-4">
                You agree to provide accurate, current, and complete information during registration and to update your information to maintain its accuracy.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.2 Account Security</h3>
              <p className="mb-4">You are responsible for:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Maintaining the confidentiality of your account credentials (username and password)</li>
                <li>All activities that occur under your account, whether authorized by you or not</li>
                <li>Notifying us immediately of any unauthorized access or security breach</li>
              </ul>
              <p className="mb-4">You must not:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Share your account credentials with any third party</li>
                <li>Allow others to use your account</li>
                <li>Use another person's account without permission</li>
              </ul>
              <p className="mb-4">
                We are not liable for any loss or damage arising from your failure to maintain account security.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.3 Account Information Accuracy</h3>
              <p className="mb-4">You represent and warrant that:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>All information you provide is truthful, accurate, and complete</li>
                <li>You will maintain and update your information as necessary</li>
                <li>You are legally permitted to use the Services</li>
                <li>You will comply with all applicable laws when using the Services</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.4 Account Termination by User</h3>
              <p className="mb-4">You may terminate your account at any time by:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Accessing account settings within the App and selecting the delete account option</li>
                <li>Contacting us at info@holein1app.com with a deletion request</li>
              </ul>
              <p className="mb-4">
                Upon account termination, your access to the Services will cease, and your account data may be deleted in accordance with our Privacy Policy. Subscription fees are non-refundable upon account termination unless otherwise required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Responsibilities and Conduct</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 Acceptable Use</h3>
              <p className="mb-4">
                You agree to use the Services only for lawful purposes and in accordance with these Terms. You are solely responsible for your conduct and any content you create, upload, transmit, or display through the Services.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 User Obligations</h3>
              <p className="mb-4">You agree to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Comply with all applicable federal, state, local, and international laws and regulations</li>
                <li>Respect the rights of other users and third parties</li>
                <li>Use the Services in a manner consistent with their intended purpose</li>
                <li>Provide accurate information when creating content or interacting with others</li>
                <li>Maintain appropriate and respectful behavior when interacting with other users</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.3 Prohibited Activities</h3>
              <p className="mb-4">You agree NOT to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Upload, post, or transmit any content that is illegal, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, pornographic, or otherwise objectionable</li>
                <li>Share content that infringes upon intellectual property rights, privacy rights, or other proprietary rights of others</li>
                <li>Post false, misleading, or deceptive information, or impersonate any person or entity</li>
                <li>Upload content containing viruses, malware, or other harmful code</li>
                <li>Attempt to gain unauthorized access to the Services, other user accounts, or computer systems</li>
                <li>Interfere with or disrupt the Services, servers, or networks connected to the Services</li>
                <li>Use any automated systems (bots, scrapers, crawlers) to access or collect data from the Services</li>
                <li>Reverse engineer, decompile, or disassemble the App</li>
                <li>Use the Services for any unauthorized commercial purpose, or advertise or promote third-party products or services without our permission</li>
                <li>Create multiple accounts for deceptive purposes, or transfer or sell your account to another party</li>
                <li>Submit fraudulent, manipulated, or misleading video recordings, including misrepresenting the location, hole, or outcome of a golf shot</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.4 Consequences of Violations</h3>
              <p className="mb-4">Violation of these Terms may result in:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Warning or notification</li>
                <li>Temporary or permanent suspension of your account</li>
                <li>Removal of content</li>
                <li>Termination of your access to the Services</li>
                <li>Legal action, including civil or criminal penalties</li>
                <li>Reporting to law enforcement authorities</li>
              </ul>
              <p className="mb-4">
                We reserve the right to investigate and take appropriate action against users who violate these Terms, in our sole discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Intellectual Property Rights</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.1 Our Intellectual Property</h3>
              <p className="mb-4">
                All content, features, functionality, software, designs, text, graphics, logos, icons, images, audio, video, data compilations, and source code of the Hole In 1 app and Services (collectively, "Company Content") are owned by Kia Kaha Labs LLC or our licensors and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.2 Limited License to Users</h3>
              <p className="mb-4">
                Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Access and use the App on your personal mobile device</li>
                <li>Use the Services for your personal, non-commercial purposes</li>
              </ul>
              <p className="mb-4">This license does not include any right to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Modify, reproduce, distribute, create derivative works, publicly display, or publicly perform any Company Content</li>
                <li>Use Company Content for commercial purposes</li>
                <li>Remove or modify any copyright, trademark, or proprietary notices</li>
                <li>Transfer, sell, or sublicense your rights to any third party</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.3 Trademarks</h3>
              <p className="mb-4">
                "Hole In 1," the Hole In 1 logo, and other marks used in connection with the Services are trademarks or registered trademarks of Kia Kaha Labs LLC. You may not use these marks without our prior written permission. All other trademarks, service marks, and logos used in the Services are the property of their respective owners.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.4 Copyright Infringement</h3>
              <p className="mb-4">
                We respect the intellectual property rights of others. If you believe that any content in the Services infringes your copyright, please contact us at info@holein1app.com with the following information:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Identification of the copyrighted work claimed to have been infringed</li>
                <li>Identification of the material that is allegedly infringing and its location in the Services</li>
                <li>Your contact information (name, address, telephone number, email address)</li>
                <li>A statement that you have a good faith belief that the disputed use is not authorized</li>
                <li>A statement, under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on behalf of the owner</li>
                <li>Your physical or electronic signature</li>
              </ul>
              <p className="mb-4">
                We will investigate and take appropriate action, which may include removing the allegedly infringing content.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.5 Repeat Infringers</h3>
              <p className="mb-4">
                We maintain a policy of terminating accounts of users who are repeat copyright infringers in appropriate circumstances.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. User-Generated Content</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">6.1 Your Content</h3>
              <p className="mb-4">
                The Services allow you to create, upload, post, and share content, including golf videos, photos, comments, messages, and other materials (collectively, "User Content"). You retain ownership of any intellectual property rights in your User Content.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">6.2 License to Company</h3>
              <p className="mb-4">
                By submitting User Content to the Services, you grant Kia Kaha Labs LLC and its affiliates a worldwide, non-exclusive, royalty-free, fully paid, sublicensable, and transferable license to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Use, reproduce, distribute, modify, adapt, prepare derivative works of, display, and perform your User Content</li>
                <li>Store, transmit, and process your User Content on our servers and through third-party services</li>
                <li>Make your User Content available to other users in accordance with your privacy settings</li>
                <li>Use your User Content for promotional and marketing purposes in connection with the Services</li>
              </ul>
              <p className="mb-4">
                This license continues even if you stop using the Services, but only for User Content you shared with other users or that was displayed publicly before termination. You may delete your User Content at any time, and the license will terminate within a reasonable time after deletion, except for copies retained in backups or as required by law.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">6.3 User Content Representations and Warranties</h3>
              <p className="mb-4">You represent and warrant that:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>You own or have the necessary rights, licenses, and permissions to submit your User Content and grant the license described above</li>
                <li>Your User Content does not and will not infringe, violate, or misappropriate any third-party intellectual property, privacy, publicity, or other rights</li>
                <li>Your User Content complies with these Terms and all applicable laws</li>
                <li>Your User Content does not contain any viruses, malware, or other harmful code</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">6.4 Content Moderation</h3>
              <p className="mb-4">We reserve the right, but have no obligation, to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Monitor, review, or edit User Content</li>
                <li>Remove or refuse to post any User Content that violates these Terms or is otherwise objectionable</li>
                <li>Take action against users who violate these Terms</li>
                <li>Cooperate with law enforcement or respond to legal requests</li>
              </ul>
              <p className="mb-4">
                We are not responsible for User Content submitted by users and do not endorse any opinions, recommendations, or advice expressed in User Content.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">6.5 Feedback and Suggestions</h3>
              <p className="mb-4">
                If you provide us with feedback, suggestions, or ideas about the Services ("Feedback"), you grant us a perpetual, irrevocable, worldwide, royalty-free license to use, modify, and incorporate the Feedback into our Services without any obligation or compensation to you.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">6.6 Content Storage and Backup</h3>
              <p className="mb-4">
                While we implement reasonable backup and storage practices, you are solely responsible for maintaining your own backups of your User Content. We are not liable for any loss, corruption, or deletion of User Content.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Subscription and Payment Terms</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.1 Subscription Plans</h3>
              <p className="mb-4">
                Hole In 1 offers subscription-based premium features and services. Subscription plans may include:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Monthly subscriptions</li>
                <li>Annual subscriptions</li>
                <li>Premium features and content</li>
                <li>Enhanced functionality</li>
              </ul>
              <p className="mb-4">
                Pricing, features, and availability of subscription plans are subject to change at our discretion.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.2 Payment Processing</h3>
              <p className="mb-4">
                All subscription payments are processed through third-party payment processors:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Apple App Store (for iOS users)</li>
                <li>Google Play Store (for Android users)</li>
                <li>RevenueCat (payment management platform)</li>
              </ul>
              <p className="mb-4">
                Payment terms are governed by the applicable payment processor's terms and conditions. We do not directly process, collect, or store your payment card information.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.3 Subscription Fees</h3>
              <p className="mb-4">
                By purchasing a subscription, you agree to pay the applicable fees as presented at the time of purchase. Fees are:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Charged in the currency specified at the time of purchase</li>
                <li>Inclusive or exclusive of taxes as required by applicable law (sales tax, VAT, GST)</li>
                <li>Subject to change with advance notice</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.4 Automatic Renewal</h3>
              <p className="mb-4">
                Subscriptions automatically renew at the end of each billing period (monthly or annually) unless you cancel before the renewal date. You authorize us (through our payment processors) to charge your payment method for the renewal fee.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.5 Cancellation</h3>
              <p className="mb-4">You may cancel your subscription at any time by:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Managing subscriptions through your Apple App Store or Google Play Store account settings</li>
                <li>Contacting customer support at info@holein1app.com</li>
              </ul>
              <p className="mb-4">
                Cancellation will take effect at the end of your current billing period. You will retain access to premium features until the end of the paid period.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.6 Price Changes</h3>
              <p className="mb-4">
                We reserve the right to change subscription prices at any time. Price changes will:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Be communicated to you in advance through the App or via email</li>
                <li>Apply to your next billing cycle after the effective date</li>
                <li>Give you the opportunity to cancel before the new price takes effect</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.7 Free Trials and Promotional Offers</h3>
              <p className="mb-4">
                We may offer free trials or promotional pricing from time to time. Terms specific to these offers will be presented at the time of enrollment. Unless you cancel before the trial period ends, you will be automatically charged the applicable subscription fee.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.8 Taxes</h3>
              <p className="mb-4">
                You are responsible for all applicable taxes related to your purchase, except for taxes based on our net income. We may collect taxes as required by law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Refund Policy</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.1 General Refund Policy</h3>
              <p className="mb-4">
                All subscription fees are generally non-refundable except as required by applicable law or as explicitly stated in these Terms.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.2 Refund Requests</h3>
              <p className="mb-4">
                Refund requests should be directed to the payment processor through which you made your purchase:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Apple App Store:</strong> Apple's refund policy applies. Contact Apple Support to request a refund.</li>
                <li><strong>Google Play Store:</strong> Google's refund policy applies. Contact Google Play Support to request a refund.</li>
              </ul>
              <p className="mb-4">
                We do not have direct control over refunds processed by these third-party payment processors.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.3 Exceptional Circumstances</h3>
              <p className="mb-4">
                In exceptional circumstances (such as technical issues preventing access to paid features), we may, at our sole discretion, offer refunds, credits, or alternative compensation. Contact us at info@holein1app.com to discuss such circumstances.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.4 Legal Rights</h3>
              <p className="mb-4">
                Nothing in this refund policy affects your statutory rights as a consumer under applicable law, including rights under consumer protection legislation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Termination and Suspension</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">9.1 Termination by You</h3>
              <p className="mb-4">
                You may terminate your account and stop using the Services at any time as described in Section 3.4.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">9.2 Termination or Suspension by Us</h3>
              <p className="mb-4">
                We reserve the right to suspend or terminate your account and access to the Services, with or without notice, for any reason, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Violation of these Terms or our policies</li>
                <li>Fraudulent, abusive, or illegal activity</li>
                <li>Requests by law enforcement or government agencies</li>
                <li>Extended periods of inactivity</li>
                <li>Technical or security reasons</li>
                <li>Discontinuation of the Services</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">9.3 Effect of Termination</h3>
              <p className="mb-4">Upon termination of your account:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Your right to access and use the Services immediately ceases</li>
                <li>We may delete your User Content and account information in accordance with our Privacy Policy</li>
                <li>You remain liable for any outstanding fees or obligations incurred before termination</li>
                <li>Sections of these Terms that by their nature should survive termination will continue to apply</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">9.4 No Refunds Upon Termination</h3>
              <p className="mb-4">
                Except as required by law, we are not obligated to provide refunds for any unused portion of your subscription upon termination or suspension of your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Disclaimers and Limitations of Liability</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.1 "AS IS" and "AS AVAILABLE"</h3>
              <p className="mb-4">
                THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT</li>
                <li>WARRANTIES THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS</li>
                <li>WARRANTIES REGARDING THE ACCURACY, RELIABILITY, OR COMPLETENESS OF CONTENT OR INFORMATION</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.2 No Professional Advice</h3>
              <p className="mb-4">
                The Services are for informational and entertainment purposes only and do not constitute professional advice of any kind. We are not responsible for any decisions you make based on information obtained through the Services.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.3 Third-Party Content and Services</h3>
              <p className="mb-4">
                The Services may contain links to third-party websites, services, or resources. We do not endorse and are not responsible for:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>The availability, accuracy, or content of third-party websites or services</li>
                <li>Any products, services, or content available through third-party sites</li>
                <li>Your interactions with third parties</li>
              </ul>
              <p className="mb-4">
                Your use of third-party services is at your own risk and subject to their terms and policies.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.4 User Content Disclaimer</h3>
              <p className="mb-4">
                We are not responsible for User Content submitted by users. We do not endorse, support, represent, or guarantee the accuracy, completeness, or reliability of any User Content. You rely on User Content at your own risk.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.5 Limitation of Liability</h3>
              <p className="mb-4">
                TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, KIA KAHA LABS LLC, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, LICENSORS, AND SERVICE PROVIDERS (COLLECTIVELY, "COMPANY PARTIES") SHALL NOT BE LIABLE FOR:
              </p>
              <p className="mb-4">
                Indirect, Incidental, Special, Consequential, or Punitive Damages, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Loss of profits, revenue, data, or goodwill</li>
                <li>Business interruption or loss of business opportunity</li>
                <li>Costs of substitute services or procurement of replacement services</li>
                <li>Any damages arising from your use of or inability to use the Services</li>
                <li>Any damages resulting from unauthorized access to your account or data</li>
                <li>Any damages resulting from errors, mistakes, or inaccuracies in content</li>
                <li>Any damages resulting from User Content or conduct of any user or third party</li>
              </ul>
              <p className="mb-4">
                WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), PRODUCT LIABILITY, STRICT LIABILITY, OR ANY OTHER LEGAL THEORY, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.6 Cap on Liability</h3>
              <p className="mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE TOTAL LIABILITY OF THE COMPANY PARTIES TO YOU FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICES SHALL NOT EXCEED THE GREATER OF:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>The total amount you paid to us in the 12 months preceding the event giving rise to the liability, OR</li>
                <li>ONE HUNDRED DOLLARS ($100 USD)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.7 Exceptions</h3>
              <p className="mb-4">
                Some jurisdictions do not allow the exclusion or limitation of certain warranties or damages. In such jurisdictions, the limitations and exclusions in this section apply only to the extent permitted by law.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.8 Basis of the Bargain</h3>
              <p className="mb-4">
                You acknowledge that the disclaimers and limitations of liability in this section reflect a reasonable and fair allocation of risk between you and the Company and are fundamental elements of the basis of the bargain between you and us. The Services would not be provided without these limitations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Indemnification</h2>
              <p className="mb-4">
                You agree to indemnify, defend, and hold harmless Kia Kaha Labs LLC and the Company Parties from and against any and all claims, liabilities, damages, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising from:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Your use or misuse of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any rights of any third party, including intellectual property, privacy, or publicity rights</li>
                <li>Your User Content</li>
                <li>Your conduct in connection with the Services</li>
                <li>Any false or misleading information you provide</li>
                <li>Your violation of any applicable laws or regulations</li>
              </ul>
              <p className="mb-4">
                We reserve the right to assume the exclusive defense and control of any matter subject to indemnification by you, and you agree to cooperate in the defense of such claims. You will not settle any claim that affects us without our prior written consent.
              </p>
              <p className="mb-4">
                This indemnification obligation will survive the termination of these Terms and your use of the Services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Dispute Resolution and Arbitration</h2>

              <h3 className="text-xl font-medium text-gray-900 mb-3">12.1 Informal Resolution</h3>
              <p className="mb-4">
                Before initiating formal dispute resolution, you agree to contact us at info@holein1app.com to attempt to resolve the dispute informally. We will attempt to resolve the dispute through good-faith negotiation. If the dispute is not resolved within 60 days, either party may proceed to arbitration or litigation as provided below.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">12.2 Binding Arbitration</h3>
              <p className="mb-4">
                PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.
              </p>
              <p className="mb-4">
                Except as provided below, any dispute, claim, or controversy arising out of or relating to these Terms, the Services, or your relationship with us (collectively, "Disputes") shall be resolved by binding arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">12.3 Arbitration Procedures</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>The arbitration will be conducted by a single neutral arbitrator</li>
                <li>The arbitration will take place in the United States, in the state where you reside or another mutually agreeable location</li>
                <li>The arbitration will be conducted in English</li>
                <li>The arbitrator's decision will be final and binding</li>
                <li>The arbitrator may award any relief available in court, including damages, injunctions, and declaratory relief</li>
                <li>Each party will bear its own costs and attorneys' fees, unless the arbitrator awards fees as permitted by law</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">12.4 Exceptions to Arbitration</h3>
              <p className="mb-4">The following Disputes are not subject to arbitration:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Claims in small claims court, provided the claim remains in small claims court</li>
                <li>Claims for injunctive or equitable relief to protect intellectual property rights</li>
                <li>Claims related to, or arising from, allegations of theft, piracy, invasion of privacy, or unauthorized use</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">12.5 Class Action Waiver</h3>
              <p className="mb-4">
                YOU AND KIA KAHA LABS LLC AGREE THAT DISPUTES SHALL BE RESOLVED ONLY ON AN INDIVIDUAL BASIS AND NOT AS A CLASS ACTION, CONSOLIDATED ACTION, OR REPRESENTATIVE ACTION. You and we waive any right to pursue claims on a class, consolidated, or representative basis.
              </p>
              <p className="mb-4">
                If the class action waiver is found to be unenforceable, the arbitration agreement in this section will be void, and the Dispute will be resolved in court.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">12.6 Jury Trial Waiver</h3>
              <p className="mb-4">
                YOU AND KIA KAHA LABS LLC WAIVE ANY RIGHT TO A JURY TRIAL. If arbitration is not required or enforceable, Disputes will be resolved by a judge sitting without a jury.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">12.7 Opt-Out Right</h3>
              <p className="mb-4">
                You have the right to opt out of this arbitration agreement. To opt out, you must send written notice to info@holein1app.com within 30 days of first accepting these Terms or within 30 days of any material change to this arbitration provision. Your opt-out notice must include your name, address, email address, and a clear statement that you wish to opt out of this arbitration agreement.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">12.8 Changes to Arbitration Terms</h3>
              <p className="mb-4">
                Notwithstanding any provision in these Terms to the contrary, we agree that if we make any material change to this arbitration provision, you may reject that change by sending us written notice within 30 days of the change, in which case your account will be governed by the arbitration provision in effect immediately before the change.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
              <p className="mb-4">
                These Terms and any Disputes shall be governed by and construed in accordance with the laws of the United States and the State of Utah, without regard to its conflict of law principles.
              </p>
              <p className="mb-4">
                If the arbitration agreement is found to be unenforceable or if you have opted out of arbitration, you agree that any litigation shall be brought exclusively in the federal or state courts located in Utah County, Utah, and you consent to the personal jurisdiction of such courts.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. General Provisions</h2>
              <p className="mb-4">
                <strong>Severability.</strong> If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such provision shall be modified to the minimum extent necessary to make it enforceable, or if it cannot be made enforceable, it shall be severed from these Terms. The remaining provisions shall continue in full force and effect.
              </p>
              <p className="mb-4">
                <strong>Entire Agreement.</strong> These Terms, together with our Privacy Policy and any additional terms applicable to specific features or services, constitute the entire agreement between you and Kia Kaha Labs LLC regarding the Services and supersede all prior or contemporaneous understandings, agreements, representations, and warranties, whether written or oral, regarding the Services.
              </p>
              <p className="mb-4">
                <strong>Waiver.</strong> Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision. Any waiver of any provision of these Terms will be effective only if in writing and signed by an authorized representative of Kia Kaha Labs LLC.
              </p>
              <p className="mb-4">
                <strong>Assignment.</strong> You may not assign, transfer, or delegate any of your rights or obligations under these Terms without our prior written consent. We may assign or transfer our rights and obligations under these Terms to any third party without restriction, including in connection with a merger, acquisition, sale of assets, or by operation of law.
              </p>
              <p className="mb-4">
                <strong>Force Majeure.</strong> We shall not be liable for any failure or delay in performing our obligations under these Terms due to circumstances beyond our reasonable control, including natural disasters, war, pandemics, government actions, or infrastructure failures.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Sponsored Content</h2>
              <p className="mb-4">
                The App may display content from third-party sponsors and partners, including logos, branding, and promotional materials. The presence of sponsored content does not constitute an endorsement by us of any sponsor's products or services. We may track which sponsored content is displayed to you in order to measure and fulfill our sponsorship obligations, as described in our Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Electronic Communications</h2>
              <p className="mb-4">
                By using the Services, you consent to receive electronic communications from us, including emails, push notifications, and in-app messages. These communications may include notices about your account, transactional information, and marketing messages (subject to your preferences). You agree that all agreements, notices, disclosures, and other communications we provide electronically satisfy any legal requirement that such communications be in writing.
              </p>
              <p className="mb-4">
                You may withdraw your consent to receive marketing communications at any time by:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Adjusting your notification preferences in the App settings</li>
                <li>Following the unsubscribe instructions in emails</li>
                <li>Contacting us at info@holein1app.com</li>
              </ul>
              <p className="mb-4">
                Withdrawing consent for marketing communications does not affect our ability to send you transactional and service-related communications.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. App Store Terms</h2>
              <p className="mb-4">
                If you download the App from the Apple App Store or Google Play Store, you acknowledge and agree to the following:
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">17.1 Apple App Store</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>These Terms are between you and Kia Kaha Labs LLC, not Apple Inc.</li>
                <li>Apple is not responsible for the App or its content</li>
                <li>Apple has no obligation to provide maintenance or support services</li>
                <li>Apple is not responsible for any product warranties</li>
                <li>Apple is not responsible for addressing any claims you or third parties have relating to the App</li>
                <li>Apple is a third-party beneficiary of these Terms and may enforce them against you</li>
                <li>You must comply with Apple's App Store Terms of Service</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">17.2 Google Play Store</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>These Terms are between you and Kia Kaha Labs LLC, not Google LLC</li>
                <li>Google is not responsible for the App or its content</li>
                <li>Google has no obligation to provide maintenance or support services</li>
                <li>Google is not liable for any claims relating to the App</li>
                <li>You must comply with Google Play's Terms of Service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">18. Survival</h2>
              <p className="mb-4">
                The following provisions shall survive termination of these Terms: Sections 5 (Intellectual Property Rights), 6.2 (License to Company), 6.5 (Feedback and Suggestions), 10 (Disclaimers and Limitations of Liability), 11 (Indemnification), 12 (Dispute Resolution and Arbitration), 13 (Governing Law), 14 (General Provisions), and other provisions that by their nature should survive.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">19. Contact Information</h2>
              <p className="mb-4">If you have any questions, concerns, or comments about these Terms or the Services, please contact us:</p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="mb-2"><strong>Kia Kaha Labs LLC</strong></p>
                <p className="mb-2">Email: <a href="mailto:info@holein1app.com" className="text-green-600 hover:text-green-700">info@holein1app.com</a></p>
                <p className="mb-4">Subject Line: Terms of Service Inquiry</p>
                <p className="mb-2">For legal notices or formal communications:</p>
                <p className="mb-2">Email: <a href="mailto:info@holein1app.com" className="text-green-600 hover:text-green-700">info@holein1app.com</a></p>
                <p className="mb-4">Subject Line: Legal Notice</p>
                <p>We will make every effort to respond to your inquiry within a reasonable time.</p>
              </div>
            </section>

            <p className="text-sm text-gray-500 mt-8 text-center">
              END OF TERMS OF SERVICE -- &copy; 2026 Kia Kaha Labs LLC. All rights reserved.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8" style={{ backgroundColor: '#F0F5E8' }}>
        <div className="container mx-auto px-4 text-center text-gray-600">
          <p>&copy; {new Date().getFullYear()} Hole In 1 App. All rights reserved.</p>
          <div className="mt-4 space-x-6">
            <a href="/privacy" className="text-green-600 hover:text-green-700">Privacy Policy</a>
            <a href="/terms" className="text-green-600 hover:text-green-700">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
